import { apiClient } from '../api/apiClient';

export interface PreferencesJSON {
  typesOfDiet?: string | null;
  typesOfDietNotes?: string | null;
  favoriteFood?: string | null;
  dislikedFood?: string | null;
  allergies?: string | null;
  allergiesNotes?: string | null;
  foodIntolerances?: string | null;
  foodIntolerancesNotes?: string | null;
  nutritionalDeficiencies?: string | null;
  nutritionalDeficienciesNotes?: string | null;
  waterIntake?: string | null;
  otherInformation?: string | null;
}

export interface HumanContextJSON {
  activityLevel?: string | null;
  cultureIdentity?: string | null;
  religiousRestrictions: string[];
  sleepPatterns?: string | null;
  stressLevels?: string | null;
  workSchedule?: string | null;
  familyStructure?: string | null;
  cookingResponsibility?: string | null;
  sleepQuality?: string | null;
  smokingStatus?: string | null;
  alcoholConsumption?: string | null;
  maritalStatus?: string | null;
}

export interface ConstraintsJSON {
  cooking?: {
    has_stove?: boolean;
    has_microwave?: boolean;
    has_refrigerator?: boolean;
    has_oven?: boolean;
    cooking_skills_level?: 'beginner' | 'intermediate' | 'advanced';
  } | null;
  budget?: {
    monthly_budget?: number;
    currency?: string;
  } | null;
  schedule?: {
    work_hours?: string;
    meal_prep_time_min?: number;
    eatingOutFrequency?: 'rarely' | 'sometimes' | 'often';
  } | null;
  wontDo: string[];
}

  export interface MedicalHistoryJSON {
  diseases?: string | null;
  diseasesNotes?: string | null;
  medication?: string | null;
  medicationNotes?: string | null;
  personalHistory?: string | null;
  personalHistoryNotes?: string | null;
  familyHistory?: string | null;
  familyHistoryNotes?: string | null;
  otherInformation?: string | null;
}

export interface MealPatternJSON {
  typicalMealsPerDay?: number | null;
  mealTimes?: Record<string, string> | null;
  snackFrequency?: string | null;
  lateNightEating?: boolean | null;
  weekendPatterns?: string | null;
}

export interface MealItemJSON {
  mealType: string;
  time: string;
  foodItems: string[];
  estimatedCalories?: number;
  estimatedProteinG?: number;
  estimatedCarbsG?: number;
  estimatedFatG?: number;
  notes?: string;
}

export interface MealDayJSON {
  day: number;
  date: string; // ISO date
  meals: MealItemJSON[];
}

export interface MealRecallJSON {
  recallPeriodDays: number;
  recallDate: string; // ISO date
  meals: MealDayJSON[];
}

export interface NutritionProfile {
  id: string;
  userId: number;
  profileVersion: number;
  isCurrent: boolean;
  capturedByDoctorId?: number | null;
  capturedViaEnum: string;
  captureDate: string; // ISO date
  bodyWeightKg?: number | null;
  activityLevel?: string | null;
  gender?: string | null;
  heightCm?: number | null;
  dateOfBirth?: string | null; // ISO date
  waistCm?: number | null;
  hipCm?: number | null;
  neckCm?: number | null;
  isPregnant?: boolean | null;
  isLactating?: boolean | null;
  lifeStage?: string | null; // Derived from dateOfBirth
  motherTongue?: string | null;
  preferencesJson: PreferencesJSON;
  humanContextJson: HumanContextJSON;
  constraintsJson: ConstraintsJSON;
  medicalHistoryJson: MedicalHistoryJSON;
  mealPatternJson?: MealPatternJSON | null;
  mealRecallJson?: MealRecallJSON | null;
  motivationReadiness?: string | null;
  previousDietAttempts: number;
  supportSystemAvailable?: boolean | null;
  technologyComfortLevel?: string | null;
  allergiesJson: { [key: string]: string }[];
  doctorNotes?: string | null;
  patientNotes?: string | null;
  age?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface NutritionProfileListResponse {
  profiles: NutritionProfile[];
  total: number;
  currentProfileId?: string | null;
}

export interface NutritionProfileCreateInput {
  userId?: number;
  bodyWeightKg?: number;
  activityLevel?: string;
  gender?: string;
  heightCm?: number;
  dateOfBirth?: string; // ISO date
  waistCm?: number;
  hipCm?: number;
  neckCm?: number;
  isPregnant?: boolean;
  isLactating?: boolean;
  lifeStage?: string; // Will be derived from dateOfBirth if not provided
  motherTongue?: string;
  preferencesJson?: PreferencesJSON;
  humanContextJson?: HumanContextJSON;
  constraintsJson?: ConstraintsJSON;
  medicalHistoryJson?: MedicalHistoryJSON;
  mealPatternJson?: MealPatternJSON;
  mealRecallJson?: MealRecallJSON;
  motivationReadiness?: string;
  previousDietAttempts?: number;
  supportSystemAvailable?: boolean;
  technologyComfortLevel?: string;
  allergiesJson?: { [key: string]: string }[];
  doctorNotes?: string;
  patientNotes?: string;
  capturedViaEnum?: string;
}

export interface NutritionProfileUpdateInput {
  bodyWeightKg?: number;
  activityLevel?: string;
  gender?: string;
  heightCm?: number;
  dateOfBirth?: string; // ISO date
  waistCm?: number;
  hipCm?: number;
  neckCm?: number;
  isPregnant?: boolean;
  isLactating?: boolean;
  lifeStage?: string; // Will be derived from dateOfBirth if not provided
  motherTongue?: string;
  preferencesJson?: PreferencesJSON;
  humanContextJson?: HumanContextJSON;
  constraintsJson?: ConstraintsJSON;
  medicalHistoryJson?: MedicalHistoryJSON;
  mealPatternJson?: any;
  mealRecallJson?: any;
  motivationReadiness?: string;
  previousDietAttempts?: number;
  supportSystemAvailable?: boolean;
  technologyComfortLevel?: string;
  allergiesJson?: { [key: string]: string }[];
  doctorNotes?: string;
  patientNotes?: string;
  capturedViaEnum?: string;
}

export interface NutritionProfileStatus {
  hasProfile: boolean;
  profileId: string | null;
  profileCreatedBy: string | null; // "doctor" or "user"
  profileCreatedAt: string | null;
  hasMealPlan: boolean;
  mealPlanId: number | null;
}

/** Result of parsing documents for nutrition profile (snake_case from API). */
export interface NutritionProfileParseResult {
  gender?: string | null;
  date_of_birth?: string | null;
  height_cm?: number | null;
  body_weight_kg?: number | null;
  waist_cm?: number | null;
  hip_cm?: number | null;
  neck_cm?: number | null;
  bmi?: number | null;
  body_fat_pct?: number | null;
  bsa?: number | null;
  fat_free_body_weight_kg?: number | null;
  fat_mass_kg?: number | null;
  subcutaneous_fat_pct?: number | null;
  visceral_fat_grade?: number | null;
  body_water_pct?: number | null;
  body_water_kg?: number | null;
  intracellular_water_kg?: number | null;
  extracellular_water_kg?: number | null;
  ecw_tbw_ratio?: number | null;
  mineral_mass_kg?: number | null;
  phase_angle?: number | null;
  skeletal_muscle_pct?: number | null;
  skeletal_muscle_kg?: number | null;
  muscle_mass_kg?: number | null;
  bone_mass_kg?: number | null;
  protein_pct?: number | null;
  protein_mass_kg?: number | null;
  bmr_kcal?: number | null;
  metabolic_age?: number | null;
  body_age?: number | null;
  body_score?: number | null;
  whr?: number | null;
  smi_kg_m2?: number | null;
  obesity_pct?: number | null;
  body_type?: string | null;
  bmi_evaluation?: string | null;
  is_pregnant?: boolean | null;
  is_lactating?: boolean | null;
  life_stage?: string | null;
  types_of_diet?: string | null;
  diet_notes?: string | null;
  favorite_food?: string | null;
  disliked_food?: string | null;
  allergies?: string | null;
  allergies_notes?: string | null;
  food_intolerances?: string | null;
  food_intolerances_notes?: string | null;
  nutritional_deficiencies?: string | null;
  nutritional_deficiencies_notes?: string | null;
  water_intake?: string | null;
  preferences_other?: string | null;
  mother_tongue?: string | null;
  marital_status?: string | null;
  motivation_readiness?: string | null;
  technology_comfort_level?: string | null;
  previous_diet_attempts?: number | null;
  support_system_available?: boolean | null;
  activity_level?: string | null;
  culture_identity?: string | null;
  religious_restrictions?: string | null;
  sleep_patterns?: string | null;
  stress_levels?: string | null;
  work_schedule?: string | null;
  family_structure?: string | null;
  cooking_responsibility?: string | null;
  sleep_quality?: string | null;
  smoking_status?: string | null;
  alcohol_consumption?: string | null;
  diseases?: string | null;
  diseases_notes?: string | null;
  medication?: string | null;
  medication_notes?: string | null;
  personal_history?: string | null;
  personal_history_notes?: string | null;
  family_history?: string | null;
  family_history_notes?: string | null;
  medical_history_other?: string | null;
  cooking_skills_level?: string | null;
  monthly_budget?: number | null;
  budget_currency?: string | null;
  work_hours?: string | null;
  meal_prep_time_min?: number | null;
  eating_out_frequency?: string | null;
  wont_do?: string | null;
  typical_meals_per_day?: number | null;
  snack_frequency?: string | null;
  weekend_patterns?: string | null;
  late_night_eating?: boolean | null;
  meal_times_notes?: string | null;
  doctor_notes?: string | null;
  patient_notes?: string | null;
}

/** Per-document extraction result (when multiple documents are parsed). */
export interface NutritionProfileExtractionItem {
  document_index: number;
  filename: string;
  extracted: NutritionProfileParseResult;
}

export interface ParseDocumentsResponse {
  extracted: NutritionProfileParseResult;
  message: string;
  /** Present when multiple documents were parsed; doctor can choose which document's value to use per field. */
  extractions?: NutritionProfileExtractionItem[];
}

export interface CurrentNutritionState {
  user_id: number;
  target_date: string;
  demographics: {
    gender?: string;
    date_of_birth?: string;
    age_years?: number;
    height_cm?: number;
    activity_level?: string;
  };
  anthropometrics: {
    height_cm?: number;
    body_weight_kg?: number;
    waist_cm?: number;
    hip_cm?: number;
    bmi?: number;
    waist_to_height_ratio?: number;
  };
  derived_metrics: Record<string, { value: number | null; unit: string | null; recorded_at: string | null; source: string | null; display_name?: string }>;
  profile_snapshot?: {
    id: string;
    version: number;
    captured_via: string;
    capture_date: string;
    gender?: string;
    height_cm?: number;
    body_weight_kg?: number;
    waist_cm?: number;
    hip_cm?: number;
    neck_cm?: number;
    date_of_birth?: string;
    life_stage?: string;
    is_pregnant?: boolean;
    is_lactating?: boolean;
    preferences?: PreferencesJSON;
    constraints?: ConstraintsJSON;
    medical_history?: MedicalHistoryJSON;
    meal_pattern?: MealPatternJSON;
    meal_recall?: any;
    motivation_readiness?: string;
    previous_diet_attempts?: number;
    support_system_available?: boolean;
    technology_comfort_level?: string;
    doctor_notes?: string;
    patient_notes?: string;
    mother_tongue?: string;
  } | null;
}

export const nutritionProfileService = {
  async getCurrentProfileForPatient(userId: number): Promise<NutritionProfile | null> {
    try {
      const resp = await apiClient.get<any>(`/api/v1/nutrition/profile/current`, {
        params: { user_id: userId },
      });
      // API now returns null (200 OK) instead of 404 when no profile exists
      // This is a valid state - patient may not have created a profile yet
      return resp.data ? fromApi(resp.data) : null;
    } catch (e: any) {
      // Handle 404 for backwards compatibility (in case API hasn't been updated yet)
      if (e?.response?.status === 404) {
        console.log('No current nutrition profile found (404 - will be changed to 200 OK with null)');
        return null;
      }
      // Re-throw other errors
      throw e;
    }
  },

  async createProfile(input: NutritionProfileCreateInput): Promise<NutritionProfile> {
    const resp = await apiClient.post<any>('/api/v1/nutrition/profile', toApiCreate(input));
    return fromApi(resp.data);
  },

  async updateProfile(profileId: string, input: NutritionProfileUpdateInput): Promise<NutritionProfile> {
    const resp = await apiClient.put<any>(`/api/v1/nutrition/profile/${profileId}`, toApiUpdate(input));
    return fromApi(resp.data);
  },

  async getProfileById(profileId: string): Promise<NutritionProfile | null> {
    const resp = await apiClient.get<any>(`/api/v1/nutrition/profile/${profileId}`);
    return resp.data ? fromApi(resp.data) : null;
  },

  async listProfiles(userId: number): Promise<NutritionProfileListResponse> {
    const resp = await apiClient.get<any>(`/api/v1/nutrition/profile/user/${userId}`, {
      params: { include_inactive: true },
    });
    return {
      profiles: (resp.data?.profiles || []).map(fromApi),
      total: resp.data?.total || 0,
      currentProfileId: resp.data?.current_profile_id ?? resp.data?.currentProfileId ?? null,
    };
  },

  async deleteProfile(profileId: string): Promise<void> {
    await apiClient.delete(`/api/v1/nutrition/profile/${profileId}`);
  },

  async getCurrentStateForPatient(userId: number): Promise<CurrentNutritionState> {
    const resp = await apiClient.get<CurrentNutritionState>(
      `/api/v1/nutrition/profile/state`,
      { params: { user_id: userId } },
    );
    return resp.data;
  },

  async getProfileStatus(userId: number): Promise<NutritionProfileStatus> {
    const resp = await apiClient.get<any>(
      `/api/v1/nutrition/profile/status`,
      { params: { user_id: userId } },
    );
    // Convert snake_case from backend to camelCase for frontend
    const data = resp.data;
    return {
      hasProfile: data.has_profile ?? data.hasProfile ?? false,
      profileId: data.profile_id ?? data.profileId ?? null,
      profileCreatedBy: data.profile_created_by ?? data.profileCreatedBy ?? null,
      profileCreatedAt: data.profile_created_at ?? data.profileCreatedAt ?? null,
      hasMealPlan: data.has_meal_plan ?? data.hasMealPlan ?? false,
      mealPlanId: data.meal_plan_id ?? data.mealPlanId ?? null,
    };
  },

  /**
   * Parse uploaded documents (images/PDFs) to extract nutrition profile and basic vitals.
   * Uses async flow: POST starts job, poll until complete. Avoids timeouts from long-running LLM parsing.
   * Optional onProgress(attempt, status) is called on each poll so the UI can show progress linked to actual status.
   */
  async parseDocuments(
    userId: number,
    files: File[],
    onProgress?: (attempt: number, status: 'pending' | 'complete' | 'failed') => void,
  ): Promise<ParseDocumentsResponse> {
    const formData = new FormData();
    formData.append('user_id', String(userId));
    files.forEach((file) => formData.append('files', file));
    const startResp = await apiClient.post<{ job_id: string; status: string; message?: string }>(
      '/api/v1/nutrition/profile/parse-documents',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000, // 30s for upload + job creation
      },
    );
    const jobId = startResp.data?.job_id;
    if (!jobId) {
      throw new Error('Server did not return a job ID');
    }
    const pollIntervalMs = 2500;
    const maxAttempts = 72; // ~3 min total
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      const pollResp = await apiClient.get<{
        status: string;
        extracted?: Record<string, unknown>;
        extractions?: NutritionProfileExtractionItem[];
        message?: string;
        error?: string;
      }>(`/api/v1/nutrition/profile/parse-documents/${jobId}`, { timeout: 10000 });
      const status = pollResp.data?.status as 'pending' | 'complete' | 'failed';
      onProgress?.(i, status === 'complete' ? 'complete' : status === 'failed' ? 'failed' : 'pending');
      if (status === 'complete') {
        const extracted = (pollResp.data?.extracted || {}) as NutritionProfileParseResult;
        const message = pollResp.data?.message || 'Extraction complete. Review and load into form if correct.';
        const extractions = pollResp.data?.extractions;
        return { extracted, message, ...(extractions && extractions.length > 0 ? { extractions } : {}) };
      }
      if (status === 'failed') {
        throw new Error(pollResp.data?.error || 'Document parsing failed');
      }
    }
    throw new Error('Parsing timed out. Please try again with fewer or smaller files.');
  },
};

// Derived Metrics types and service
export interface ReferenceRange {
  sex: string;
  age_min: number;
  age_max: number | null;
  min_value: number | null;
  max_value: number | null;
  interpretation: string | null;
}

export interface DerivedMetricSummaryItem {
  metric_key: string;
  display_name: string;
  category: string | null;
  value: number | null;
  unit: string | null;
  date: string | null;
  recency_date: string | null;
  within_reference: boolean | null;
  /** e.g. "Poor", "Good", "Excellent" when value falls in a tiered band */
  interpretation_matched: string | null;
  reference_display: string | null;
  reference_unit: string | null;
  formula: string | null;
  description: string | null;
  reference_ranges: ReferenceRange[];
  missing_reason: string | null;
  missing_inputs: any[];
  /** Provenance: vitals | derived; when vitals, vital_data_source indicates document vs device */
  value_origin?: string | null;
  vital_data_source?: string | null;
  /** e.g. document_extraction, doctor_portal (display: Device/Manual), vitals, derived, aggregate */
  source?: string | null;
}

export interface DerivedMetricsSummaryResponse {
  user_id: number;
  computed_at: string;
  items: DerivedMetricSummaryItem[];
}

export const derivedMetricsService = {
  async getSummary(userId: number, days: number = 90, targetDate?: string): Promise<DerivedMetricsSummaryResponse> {
    const params: any = { user_id: userId, days };
    if (targetDate) {
      params.target_date = targetDate;
    }
    const resp = await apiClient.get<DerivedMetricsSummaryResponse>(
      `/api/v1/derived-metrics/summary`,
      { params },
    );
    return resp.data;
  },
  async saveInsightsVitals(
    patientId: number,
    payload: DoctorVitalsSubmissionRequest,
  ): Promise<DoctorVitalsSubmissionResponse> {
    const resp = await apiClient.post<DoctorVitalsSubmissionResponse>(
      `/api/v1/doctors/patients/${patientId}/vitals`,
      payload,
    );
    return resp.data;
  },

  /** Save composite score values (wellness, cmri, etc.) to health_derived_scores. Used when values are from document or edited in Insights. */
  async saveInsightsDerivedScores(
    patientId: number,
    payload: DoctorDerivedScoresSubmissionRequest,
  ): Promise<DoctorDerivedScoresSubmissionResponse> {
    const resp = await apiClient.post<DoctorDerivedScoresSubmissionResponse>(
      `/api/v1/doctors/patients/${patientId}/derived-scores`,
      payload,
    );
    return resp.data;
  },
};

export interface DoctorVitalsSubmissionEntry {
  metric_type: string;
  value: number;
  unit: string;
  recorded_at: string;
  notes?: string;
  source_device?: string;
  confidence_score?: number;
  /** When value is from document extraction (e.g. Nutrition Profile), pass 'document_extraction' so aggregation stores it and Insights shows 'From document'. */
  data_source?: string;
}

export interface DoctorVitalsSubmissionRequest {
  entries: DoctorVitalsSubmissionEntry[];
}

export interface DoctorVitalsSubmissionResponse {
  success: boolean;
  message: string;
  processedCount: number;
  aggregationStatus?: string;
  errors?: string[] | null;
}

export interface DoctorDerivedScoreEntry {
  score_name: string;
  value: number;
  recorded_at: string;
}

export interface DoctorDerivedScoresSubmissionRequest {
  entries: DoctorDerivedScoreEntry[];
}

export interface DoctorDerivedScoresSubmissionResponse {
  success: boolean;
  message: string;
  processed_count: number;
  errors?: string[] | null;
}

function toApiPreferences(p?: PreferencesJSON) {
  if (!p) return undefined;
  // Always return an object with all fields, even if some values are null
  // This ensures the backend receives the complete preferences data structure
  // Include all fields explicitly to ensure they are saved even if null
  return {
    types_of_diet: p.typesOfDiet !== undefined ? (p.typesOfDiet && p.typesOfDiet.trim() ? p.typesOfDiet.trim() : null) : null,
    types_of_diet_notes: p.typesOfDietNotes !== undefined ? (p.typesOfDietNotes && p.typesOfDietNotes.trim() ? p.typesOfDietNotes.trim() : null) : null,
    favorite_food: p.favoriteFood !== undefined ? p.favoriteFood : null,
    disliked_food: p.dislikedFood !== undefined ? p.dislikedFood : null,
    allergies: p.allergies !== undefined ? p.allergies : null,
    allergies_notes: p.allergiesNotes !== undefined ? p.allergiesNotes : null,
    food_intolerances: p.foodIntolerances !== undefined ? p.foodIntolerances : null,
    food_intolerances_notes: p.foodIntolerancesNotes !== undefined ? p.foodIntolerancesNotes : null,
    nutritional_deficiencies: p.nutritionalDeficiencies !== undefined ? p.nutritionalDeficiencies : null,
    nutritional_deficiencies_notes: p.nutritionalDeficienciesNotes !== undefined ? p.nutritionalDeficienciesNotes : null,
    water_intake: p.waterIntake !== undefined ? p.waterIntake : null,
    other_information: p.otherInformation !== undefined ? p.otherInformation : null,
  };
}

function toApiHumanContext(h?: HumanContextJSON) {
  if (!h) return undefined;
  return {
    activity_level: h.activityLevel ?? null,
    culture_identity: h.cultureIdentity && h.cultureIdentity.trim() ? h.cultureIdentity.trim() : null,
    religious_restrictions: h.religiousRestrictions || [],
    sleep_patterns: h.sleepPatterns && h.sleepPatterns.trim() ? h.sleepPatterns.trim() : null,
    stress_levels: h.stressLevels && h.stressLevels.trim() ? h.stressLevels.trim() : null,
    work_schedule: h.workSchedule && h.workSchedule.trim() ? h.workSchedule.trim() : null,
    family_structure: h.familyStructure && h.familyStructure.trim() ? h.familyStructure.trim() : null,
    cooking_responsibility: h.cookingResponsibility && h.cookingResponsibility.trim() ? h.cookingResponsibility.trim() : null,
    sleep_quality: h.sleepQuality ?? null,
    smoking_status: h.smokingStatus ?? null,
    alcohol_consumption: h.alcoholConsumption ?? null,
    marital_status: h.maritalStatus && h.maritalStatus.trim() ? h.maritalStatus.trim() : null,
  };
}

function toApiConstraints(c?: ConstraintsJSON) {
  if (!c) return undefined;
  return {
    cooking: c.cooking || null,
    budget: c.budget || null,
    schedule: c.schedule || null,
    won_t_do: c.wontDo || [],
  };
}

function toApiMedicalHistory(m?: MedicalHistoryJSON) {
  if (!m) return undefined;
  return {
    diseases: m.diseases ?? null,
    diseases_notes: m.diseasesNotes ?? null,
    medication: m.medication ?? null,
    medication_notes: m.medicationNotes ?? null,
    personal_history: m.personalHistory ?? null,
    personal_history_notes: m.personalHistoryNotes ?? null,
    family_history: m.familyHistory ?? null,
    family_history_notes: m.familyHistoryNotes ?? null,
    other_information: m.otherInformation ?? null,
  };
}

function toApiMealPattern(m?: MealPatternJSON) {
  if (!m) return undefined;
  // Always return an object, even if some values are null
  // This ensures the backend receives the meal pattern data structure
  const result: any = {};
  if (m.typicalMealsPerDay !== undefined) result.typical_meals_per_day = m.typicalMealsPerDay;
  if (m.mealTimes !== undefined) result.meal_times = m.mealTimes;
  if (m.snackFrequency !== undefined) result.snack_frequency = m.snackFrequency;
  if (m.lateNightEating !== undefined) result.late_night_eating = m.lateNightEating;
  if (m.weekendPatterns !== undefined) result.weekend_patterns = m.weekendPatterns;
  // Return undefined only if the object is completely empty
  return Object.keys(result).length > 0 ? result : undefined;
}

function toApiMealRecall(r?: MealRecallJSON) {
  if (!r) return undefined;
  return {
    recall_period_days: r.recallPeriodDays,
    recall_date: r.recallDate,
    meals: (r.meals || []).map((d) => ({
      day: d.day,
      date: d.date,
      meals: (d.meals || []).map((mi) => ({
        meal_type: mi.mealType,
        time: mi.time,
        food_items: mi.foodItems,
        estimated_calories: mi.estimatedCalories,
        estimated_protein_g: mi.estimatedProteinG,
        estimated_carbs_g: mi.estimatedCarbsG,
        estimated_fat_g: mi.estimatedFatG,
        notes: mi.notes,
      })),
    })),
  };
}

function toApiCreate(input: NutritionProfileCreateInput) {
  return {
    user_id: input.userId,
    body_weight_kg: input.bodyWeightKg,
    activity_level: input.activityLevel,
    gender: input.gender,
    height_cm: input.heightCm,
    date_of_birth: input.dateOfBirth,
    waist_cm: input.waistCm,
    hip_cm: input.hipCm,
    neck_cm: input.neckCm,
    is_pregnant: input.isPregnant,
    is_lactating: input.isLactating,
    life_stage: input.lifeStage,
    mother_tongue: input.motherTongue && input.motherTongue.trim() ? input.motherTongue.trim() : null,
    preferences_json: toApiPreferences(input.preferencesJson),
    human_context_json: toApiHumanContext(input.humanContextJson),
    constraints_json: toApiConstraints(input.constraintsJson),
    medical_history_json: toApiMedicalHistory(input.medicalHistoryJson),
    meal_pattern_json: toApiMealPattern(input.mealPatternJson),
    meal_recall_json: toApiMealRecall(input.mealRecallJson),
    motivation_readiness: input.motivationReadiness,
    previous_diet_attempts: input.previousDietAttempts,
    support_system_available: input.supportSystemAvailable,
    technology_comfort_level: input.technologyComfortLevel,
    allergies_json: input.allergiesJson,
    doctor_notes: input.doctorNotes && input.doctorNotes.trim() ? input.doctorNotes.trim() : null,
    patient_notes: input.patientNotes && input.patientNotes.trim() ? input.patientNotes.trim() : null,
    captured_via_enum: input.capturedViaEnum,
  };
}

function toApiUpdate(input: NutritionProfileUpdateInput) {
  const result: any = {};
  
  // Only include fields that are explicitly provided (not undefined)
  // This ensures we don't overwrite existing values with undefined
  if (input.bodyWeightKg !== undefined) result.body_weight_kg = input.bodyWeightKg;
  if (input.activityLevel !== undefined) result.activity_level = input.activityLevel;
  if (input.gender !== undefined) result.gender = input.gender;
  if (input.heightCm !== undefined) result.height_cm = input.heightCm;
  if (input.dateOfBirth !== undefined) result.date_of_birth = input.dateOfBirth;
  if (input.waistCm !== undefined) result.waist_cm = input.waistCm;
  if (input.hipCm !== undefined) result.hip_cm = input.hipCm;
  if (input.neckCm !== undefined) result.neck_cm = input.neckCm;
  if (input.lifeStage !== undefined) result.life_stage = input.lifeStage;
  if (input.motherTongue !== undefined) result.mother_tongue = input.motherTongue && input.motherTongue.trim() ? input.motherTongue.trim() : null;
  if (input.preferencesJson !== undefined) result.preferences_json = toApiPreferences(input.preferencesJson);
  if (input.humanContextJson !== undefined) result.human_context_json = toApiHumanContext(input.humanContextJson);
  if (input.constraintsJson !== undefined) result.constraints_json = toApiConstraints(input.constraintsJson);
  if (input.medicalHistoryJson !== undefined) result.medical_history_json = toApiMedicalHistory(input.medicalHistoryJson);
  // Always include mealPatternJson if it exists (even if it's an empty object with defaults)
  // This ensures meal pattern data is saved when the user has filled it in
  if (input.mealPatternJson !== undefined && input.mealPatternJson !== null) {
    result.meal_pattern_json = toApiMealPattern(input.mealPatternJson);
  }
  if (input.mealRecallJson !== undefined) result.meal_recall_json = toApiMealRecall(input.mealRecallJson);
  if (input.motivationReadiness !== undefined) result.motivation_readiness = input.motivationReadiness;
  if (input.previousDietAttempts !== undefined) result.previous_diet_attempts = input.previousDietAttempts;
  if (input.technologyComfortLevel !== undefined) result.technology_comfort_level = input.technologyComfortLevel;
  if (input.allergiesJson !== undefined) result.allergies_json = input.allergiesJson;
  if (input.doctorNotes !== undefined) {
    result.doctor_notes = input.doctorNotes && input.doctorNotes.trim() ? input.doctorNotes.trim() : null;
  }
  if (input.patientNotes !== undefined) {
    result.patient_notes = input.patientNotes && input.patientNotes.trim() ? input.patientNotes.trim() : null;
  }
  if (input.capturedViaEnum !== undefined) result.captured_via_enum = input.capturedViaEnum;
  
  // Explicitly include boolean fields even if they are false or null
  // This ensures the backend receives them and can update them properly
  if (input.isPregnant !== undefined) {
    result.is_pregnant = input.isPregnant;
  }
  if (input.isLactating !== undefined) {
    result.is_lactating = input.isLactating;
  }
  if (input.supportSystemAvailable !== undefined) {
    result.support_system_available = input.supportSystemAvailable;
  }
  
  return result;
}

function fromApi(data: any): NutritionProfile {
  return {
    id: data.id,
    userId: data.user_id,
    profileVersion: data.profile_version,
    isCurrent: data.is_current,
    capturedByDoctorId: data.captured_by_doctor_id ?? null,
    capturedViaEnum: data.captured_via_enum,
    captureDate: data.capture_date,
    bodyWeightKg: data.body_weight_kg ?? null,
    activityLevel: data.activity_level ?? null,
    gender: data.gender ?? null,
    heightCm: data.height_cm ?? null,
    dateOfBirth: data.date_of_birth ?? null,
    waistCm: data.waist_cm ?? null,
    hipCm: data.hip_cm ?? null,
    neckCm: data.neck_cm ?? null,
    isPregnant: data.is_pregnant ?? null,
    isLactating: data.is_lactating ?? null,
    lifeStage: data.life_stage ?? null,
    motherTongue: data.mother_tongue ?? null,
    preferencesJson: {
      typesOfDiet: data.preferences_json?.types_of_diet ?? null,
      typesOfDietNotes: data.preferences_json?.types_of_diet_notes ?? null,
      favoriteFood: data.preferences_json?.favorite_food ?? null,
      dislikedFood: data.preferences_json?.disliked_food ?? null,
      allergies: data.preferences_json?.allergies ?? null,
      allergiesNotes: data.preferences_json?.allergies_notes ?? null,
      foodIntolerances: data.preferences_json?.food_intolerances ?? null,
      foodIntolerancesNotes: data.preferences_json?.food_intolerances_notes ?? null,
      nutritionalDeficiencies: data.preferences_json?.nutritional_deficiencies ?? null,
      nutritionalDeficienciesNotes: data.preferences_json?.nutritional_deficiencies_notes ?? null,
      waterIntake: data.preferences_json?.water_intake ?? null,
      otherInformation: data.preferences_json?.other_information ?? null,
    },
    humanContextJson: {
      activityLevel: data.human_context_json?.activity_level ?? null,
      cultureIdentity: data.human_context_json?.culture_identity ?? null,
      religiousRestrictions: data.human_context_json?.religious_restrictions || [],
      sleepPatterns: data.human_context_json?.sleep_patterns ?? null,
      stressLevels: data.human_context_json?.stress_levels ?? null,
      workSchedule: data.human_context_json?.work_schedule ?? null,
      familyStructure: data.human_context_json?.family_structure ?? null,
      cookingResponsibility: data.human_context_json?.cooking_responsibility ?? null,
      sleepQuality: data.human_context_json?.sleep_quality ?? null,
      smokingStatus: data.human_context_json?.smoking_status ?? null,
      alcoholConsumption: data.human_context_json?.alcohol_consumption ?? null,
      maritalStatus: data.human_context_json?.marital_status ?? null,
    },
    constraintsJson: {
      cooking: data.constraints_json?.cooking || null,
      budget: data.constraints_json?.budget || null,
      schedule: data.constraints_json?.schedule || null,
      wontDo: data.constraints_json?.won_t_do || [],
    },
    medicalHistoryJson: {
      diseases: data.medical_history_json?.diseases ?? null,
      diseasesNotes: data.medical_history_json?.diseases_notes ?? null,
      medication: data.medical_history_json?.medication ?? null,
      medicationNotes: data.medical_history_json?.medication_notes ?? null,
      personalHistory: data.medical_history_json?.personal_history ?? null,
      personalHistoryNotes: data.medical_history_json?.personal_history_notes ?? null,
      familyHistory: data.medical_history_json?.family_history ?? null,
      familyHistoryNotes: data.medical_history_json?.family_history_notes ?? null,
      otherInformation: data.medical_history_json?.other_information ?? null,
    },
    mealPatternJson: data.meal_pattern_json
      ? {
          typicalMealsPerDay: data.meal_pattern_json?.typical_meals_per_day ?? null,
          mealTimes: data.meal_pattern_json?.meal_times ?? null,
          snackFrequency: data.meal_pattern_json?.snack_frequency ?? null,
          lateNightEating: data.meal_pattern_json?.late_night_eating ?? null,
          weekendPatterns: data.meal_pattern_json?.weekend_patterns ?? null,
        }
      : null,
    mealRecallJson: data.meal_recall_json
      ? {
          recallPeriodDays: data.meal_recall_json?.recall_period_days,
          recallDate: data.meal_recall_json?.recall_date,
          meals: (data.meal_recall_json?.meals || []).map((d: any) => ({
            day: d.day,
            date: d.date,
            meals: (d.meals || []).map((mi: any) => ({
              mealType: mi.meal_type,
              time: mi.time,
              foodItems: mi.food_items,
              estimatedCalories: mi.estimated_calories,
              estimatedProteinG: mi.estimated_protein_g,
              estimatedCarbsG: mi.estimated_carbs_g,
              estimatedFatG: mi.estimated_fat_g,
              notes: mi.notes,
            })),
          })),
        }
      : null,
    motivationReadiness: data.motivation_readiness ?? null,
    previousDietAttempts: data.previous_diet_attempts ?? 0,
    supportSystemAvailable: data.support_system_available ?? null,
    technologyComfortLevel: data.technology_comfort_level ?? null,
    allergiesJson: data.allergies_json || [],
    doctorNotes: data.doctor_notes ?? null,
    patientNotes: data.patient_notes ?? null,
    age: data.age ?? null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}
